import type { CSSObject } from '@emotion/react'
import {
  Color,
  FontWeight,
  Size,
  Spacing,
  Typeface,
  Typography,
} from '@opallabs/gem'
import type { WithRequiredTheme } from '@opallabs/lapidary'

type StyleProps = WithRequiredTheme

export const colorBlock: CSSObject = {
  // @note `backgroundColor` currently doesn't follow the theme.
  backgroundColor: Color.light.Surface.A200,
  borderStartStartRadius: Size.Rounding * 5,
  borderStartEndRadius: Size.Rounding * 5,
  mixBlendMode: 'difference',
  position: 'absolute',
  opacity: 0.8,
  overflow: 'hidden',
  inset: 0,
  zIndex: 0,
}

export const container: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  fontFamily: Typeface.Default,
  maxInlineSize: Spacing.M * 46.25,
}

export const explanation: CSSObject = {
  ...Typography.H100,
  marginBlockEnd: Spacing.M,
}

export const learnMore = ({ theme }: StyleProps): CSSObject => ({
  ...Typography.T500,
  fontWeight: FontWeight.Bold,
  lineHeight: `${Spacing.L}px`,
  textDecoration: 'underline',

  '&:link': {
    color: Color[theme].Text.Default,
  },
})

export const logo: CSSObject = {
  marginBlockEnd: Spacing.M * 2,
}

export const logoOverlay: CSSObject = {
  opacity: 0.06,
  transform: [
    // glyph is rotated -12°
    `rotate(-${360 / 12 / 1000}turn)`,
    'scale(85.7)',
    // `translateX` and `translateY` move to a nice abstract part of the logo
    'translateX(34%)',
    'translateY(-8%)',
  ].join(' '),
}

export const sharedBy: CSSObject = {
  ...Typography.H300,
  marginBlockEnd: Spacing.S * 1.5,
}

export const themeBlock = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N200,
  color: Color[theme].Text.Default,
  alignItems: 'center',
  borderStartStartRadius: Size.Rounding * 5,
  borderStartEndRadius: Size.Rounding * 5,
  display: 'flex',
  justifyContent: 'center',
  paddingBlock: Spacing.M * 3.25,
  // `paddingInline` ensures there's always some visual space on the sides,
  // even in a small viewport or at a high zoom level.
  paddingInline: Spacing.M * 1.25,
  position: 'relative',
})

export const themeBlockContent: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  maxInlineSize: Spacing.M * 27.5,
  paddingInlineEnd: Spacing.M * 2.5,
  zIndex: 1,
}
